import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { Task } from "@/api.types.v2";
import { useSession } from "@/services/auth0/session";
import type {
  CenterIntakeObligationPage,
  IntakeObservationInput,
  IntakeReceiptInput,
} from "@/features/center-intake-obligations/types";

export interface AdminIntakeQuery {
  page: number;
  view: "outstanding" | "received" | "history";
  search: string;
  operatorId: string;
  sourceType: string;
  carrierIds: string;
  dispatchCenterIds?: string;
}
const prefix = ["admin", "center-intake-obligations"];
const endpoint = `${import.meta.env.VITE_API_URL}/api/v2/admin/center-intake-obligations`;

export function useAdminIntakeObligations(input: AdminIntakeQuery) {
  const { token, user, hasRole } = useSession();
  return useQuery({
    queryKey: [...prefix, user?.id, input],
    enabled: !!token && !!user?.id && hasRole("ADMIN"),
    retry: false,
    queryFn: async () =>
      (
        await axios.get<CenterIntakeObligationPage>(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            ...input,
            search: input.search || undefined,
            operatorId: input.operatorId || undefined,
            sourceType: input.sourceType || undefined,
          },
        })
      ).data,
  });
}
export function useAdminIntakeCommands() {
  const { token, user, hasRole } = useSession();
  const client = useQueryClient();
  const auth = () => {
    if (!token || !user?.id || !hasRole("ADMIN"))
      throw new Error("Admin access required");
    return { headers: { Authorization: `Bearer ${token}` } };
  };
  const invalidate = async (receipt: boolean) => {
    await Promise.all([
      client.invalidateQueries({ queryKey: prefix }),
      ...(receipt
        ? [
            client.invalidateQueries({ queryKey: ["tasks"] }),
            client.invalidateQueries({ queryKey: ["trips"] }),
          ]
        : []),
    ]);
  };
  const receive = useMutation({
    retry: false,
    mutationFn: async (input: IntakeReceiptInput) =>
      (
        await axios.post<{
          data: { receipts: { id: string; timing: string }[] };
        }>(`${endpoint}/receive`, input, auth())
      ).data.data,
    onSettled: () => invalidate(true),
  });
  const report = useMutation({
    retry: false,
    mutationFn: async ({ id, ...input }: IntakeObservationInput) =>
      (await axios.post(`${endpoint}/${id}/discrepancies`, input, auth())).data
        .data,
    onSettled: () => invalidate(false),
  });
  return { receive, report };
}

type AdminIntakeSourceTask = Omit<Task, "proof"> & {
  proof: NonNullable<Task["proof"]> | null;
};

/** Exact original work bypasses the operational backlog's presentation filters. */
export function useAdminIntakeSourceTask(taskId: string, scopeKey: string) {
  const { token, user, hasRole } = useSession();
  return useQuery({
    queryKey: ["admin", "intake-source-task", user?.id, scopeKey, taskId],
    enabled: !!token && !!user?.id && hasRole("ADMIN"),
    retry: false,
    queryFn: async () =>
      (
        await axios.get<AdminIntakeSourceTask>(
          `${import.meta.env.VITE_API_URL}/api/v2/tasks/${taskId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ).data,
  });
}
